import React from 'react';
import { useIntl } from 'react-intl';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  BURNDOWN_COLORS,
  CHART_COLORS,
  CHART_TYPES,
  getYAxisLabelConfig,
  shouldShowYAxisLabel,
} from '../constants';
import { useContainerSize } from '../../../../../../js/useContainerSize';
import CustomTick from '../CustomTick';
import CustomTooltip from '../CustomTooltip';
import EmptyState from '../Empty/EmptyState';
import LoadingState from '../Loadinng/LoadingState';

const CHART_MARGIN = { top: 20, right: 20, left: 0, bottom: 5 };

const GRID_CONFIG = {
  strokeDasharray: '3 3',
  stroke: CHART_COLORS.GRID_STROKE,
  horizontal: true,
  vertical: false,
};

const AXIS_LINE_CONFIG = { stroke: CHART_COLORS.AXIS_STROKE };
const TICK_LINE_CONFIG = { stroke: CHART_COLORS.AXIS_STROKE };
const Y_TICK_CONFIG = { fill: CHART_COLORS.AXIS_STROKE, fontSize: 12 };

const X_AXIS_CONFIG = {
  angle: -45,
  textAnchor: 'end',
  height: 60,
};

const AREA_CONFIG = {
  type: 'monotone',
  strokeWidth: 2,
};

const TOOLTIP_CONFIG = {
  cursor: { strokeDasharray: '3 3' },
};

const StyledXAxis = ({ label, labelEntities, labelType }) => (
  <XAxis
    dataKey="name"
    {...X_AXIS_CONFIG}
    tick={(props) => (
      <CustomTick
        labelEntities={labelEntities}
        labelType={labelType}
        {...props}
      />
    )}
    axisLine={AXIS_LINE_CONFIG}
    tickLine={TICK_LINE_CONFIG}
  />
);

const StyledYAxis = ({ label }) => (
  <YAxis
    allowDecimals={false}
    axisLine={AXIS_LINE_CONFIG}
    tickLine={TICK_LINE_CONFIG}
    tick={Y_TICK_CONFIG}
    label={label ? getYAxisLabelConfig(label) : undefined}
  />
);

const BurndownGraph = ({
  chartData,
  isLoading,
  yAxisLabel = '',
  xAxisLabel = '',
  xLabelType,
  xLabelEntities,
  handleSliceClick = () => {},
}) => {
  const intl = useIntl();
  // Callback ref, not `useEffect` + `useRef`: this component returns early
  // while loading/empty, so the wrapper div does not exist on mount — a
  // mount-time effect would find a null ref and never observe anything.
  const { ref: wrapperRef, size: containerSize } = useContainerSize();

  // Hidden when the card is too short for the rotated title to render
  // without being clipped / colliding with the top tick (see constants.js).
  const showYAxisLabel = shouldShowYAxisLabel(yAxisLabel, containerSize);

  if (isLoading) {
    return <LoadingState type={CHART_TYPES.BURNDOWN} />;
  }

  if (!chartData?.length) {
    return <EmptyState type={CHART_TYPES.BURNDOWN} />;
  }

  const totalLabel = intl.formatMessage({
    id: 'TOTAL',
    defaultMessage: 'Total',
  });

  const remainingLabel = intl.formatMessage({
    id: 'REMAINING',
    defaultMessage: 'Remaining',
  });

  return (
    <div ref={wrapperRef} className="w-full h-full flex flex-col">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={CHART_MARGIN}
          onClick={(sliceData) => handleSliceClick(sliceData, chartData)}
        >
          <defs>
            <linearGradient id="colorTotalBurndown" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={BURNDOWN_COLORS.totalStop} stopOpacity={0.3} />
              <stop offset="95%" stopColor={BURNDOWN_COLORS.remainingStop_2} stopOpacity={0.1} />
            </linearGradient>
            <linearGradient
              id="colorRemainingBurndown"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={BURNDOWN_COLORS.remainingStop_1} stopOpacity={0.5} />
              <stop offset="95%" stopColor={BURNDOWN_COLORS.remainingStop_1} stopOpacity={0.2} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_CONFIG} />
          <StyledXAxis
            label={xAxisLabel}
            labelType={xLabelType}
            labelEntities={xLabelEntities}
          />
          <StyledYAxis label={showYAxisLabel ? yAxisLabel : ''} />
          <Tooltip
            content={
              <CustomTooltip
                xLabelEntities={xLabelEntities}
                xLabelType={xLabelType}
                isBurnChart={true}
              />
            }
            {...TOOLTIP_CONFIG}
          />
          <Area
            dataKey="total"
            name={totalLabel}
            stroke={BURNDOWN_COLORS.total}
            fill="url(#colorTotalBurndown)"
            strokeWidth={2}
            dot={false}
            activeDot={false}
            {...AREA_CONFIG}
          />
          <Area
            dataKey="remaining"
            name={remainingLabel}
            stroke={BURNDOWN_COLORS.remaining}
            fill="url(#colorRemainingBurndown)"
            strokeWidth={2}
            dot={false}
            activeDot={{
              r: 5,
              fill: BURNDOWN_COLORS.remaining,
              stroke: CHART_COLORS.WHITE,
              strokeWidth: 2,
              cursor: 'pointer',
              onClick: (e, payload) =>
                handleSliceClick(payload?.payload, chartData),
            }}
            {...AREA_CONFIG}
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-end gap-6 mt-2 pb-1">
        <div className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: BURNDOWN_COLORS.total }}
          />
          <span className="text-sm text-gray-600">{totalLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: BURNDOWN_COLORS.remaining }}
          />
          <span className="text-sm text-gray-600">{remainingLabel}</span>
        </div>
      </div>
    </div>
  );
};

export default BurndownGraph;
